import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  type ElementDragPayload,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import {
  type DraggingItem,
  type DragOverItem,
  DragType,
  useDragItems,
  type VerticalPositions,
} from '@databases/drag';
import { type ScrollPosition } from '@databases/layout/scroll-position';
import { usePrevious } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectIsEntireEntrySelected,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { doFillValues } from '@databases/redux/table-layout/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '@databases/utils/constants';
import { areAllFieldsHidden } from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectValueIdFromThreadIdOptional } from '@databases/utils/redux/comments/selectors';
import { batch } from '@databases/utils/redux/redux';
import { getEntryIdFromValueId } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import { sum } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnimateItems, useRefMapWithSizes } from '../../../lazy-render';
import { shouldShowEntryPrefix } from '../../../utils';
import { tableRowMinHeight } from '../../styled';

import { type EntryIsSelectedMap } from './EntryPrefix';
import EntrySkeleton from './EntrySkeleton';
import TableLayoutEmptyStateRow from './TableLayoutEmptyStateRow';
import TableLayoutRow from './TableLayoutRow';
import useHasNumberedEntries from './UseHasNumberedEntries';

export const EMPTY_ITEM = 'empty';
export const ADD_ITEM = 'add';
export const DRAG_ITEM = 'drag';
export const GAP_ITEM = 'gap';
const TOOLBAR_PADDING_FALLBACK = 120;
const HEADER_ROW_PADDING_FALLBACK = 52;

/** The height to assume for rows that have not been rendered before. */
const FALLBACK_ROW_HEIGHT = tableRowMinHeight;

function extractIndex(data: Record<string, unknown>) {
  const { index } = data;
  if (typeof index !== 'number') {
    return null;
  }
  return index;
}

export interface TableLayoutRowsProps {
  contentId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
  renderSection: ScrollPosition;
  currentWidths: Array<number | undefined>;
  containerTop: number;
  containerWidth: number;
}

const TableLayoutRows = React.memo(
  React.forwardRef<HTMLTableSectionElement, TableLayoutRowsProps>(
    function TableLayoutRows(
      {
        contentId,
        isContentEditable,
        canEditFields,
        canEditContent,
        editingIds,
        setEditingIds,
        renderSection,
        currentWidths,
        containerWidth,
        containerTop,
      },
      tableBodyRef
    ) {
      const { view } = useView();
      const allFieldsHidden = areAllFieldsHidden(view.settings);
      const { focusedCommentThreadId } = useCommentStateContext();

      const [
        entryIds,
        unfilteredEntryIds,
        fieldCount,
        isSortingActive,
        isFilterActive,
        entryIsSelectedMap,
        entryIdForFocusedComment,
        selectedEntries,
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseDraftByContentId(state, {
          contentId,
          viewSettings: view.settings,
          includeDeleted: true,
          ignoredEntries: Array.from(editingIds).map((idPair) => idPair.entryId),
        });

        const isFilterActive = view.settings?.filter;

        const unfilteredDatabase = isFilterActive
          ? selectDatabaseDraftByContentId(state, {
              contentId,
              includeDeleted: true,
              ignoredEntries: Array.from(editingIds).map((idPair) => idPair.entryId),
            })
          : database;

        const entryIsSelectedMap: EntryIsSelectedMap = database.entries.reduce(
          (acc: EntryIsSelectedMap, entryId: string) => {
            acc[entryId] = selectIsEntireEntrySelected(state, {
              entryId,
            });
            return acc;
          },
          {} as EntryIsSelectedMap
        );

        const focusedThreadValueId = focusedCommentThreadId
          ? selectValueIdFromThreadIdOptional(state, {
              contentId,
              threadId: focusedCommentThreadId,
            })
          : undefined;
        const entryIdForFocusedComment = getEntryIdFromValueId(
          focusedThreadValueId ?? ''
        );

        const selectedEntries = selectSelectedEntryIds(state, {
          contentId,
        });

        return [
          database.entries,
          unfilteredDatabase.entries,
          database.fields.length,
          view.settings?.sorts?.length > 0,
          isFilterActive,
          entryIsSelectedMap,
          entryIdForFocusedComment,
          selectedEntries,
        ] as const;
      });

      const [dragOver, setDragOver] = useState<DragOverItem>();
      /** The IDs of the rows to show. A row can be an entry row, the "empty state" row or the "add entry" row. */
      const rowIds = useMemo(() => {
        const result = [...(entryIds.length === 0 ? [EMPTY_ITEM] : entryIds)];

        if (canEditContent) {
          result.push(ADD_ITEM);
        }

        return result;
      }, [entryIds, canEditContent]);

      const rowRefMap = useRefMapWithSizes({
        getPositions: ({ sizes }) => {
          const rowPositions: Record<string, VerticalPositions> = {};
          let currentTop = 0;
          for (const entryId of rowIds) {
            const top = currentTop;
            const height =
              entryId === DRAG_ITEM ? 0 : sizes[entryId]?.height ?? FALLBACK_ROW_HEIGHT;
            rowPositions[entryId] = { top, height };
            currentTop += height;
          }

          return rowPositions;
        },
      });

      const client = useAnalytics();
      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const [draggingItem, setDraggingItem] = useState<DraggingItem>();
      const onDrop = useCallback(
        (draggingItem: DraggingItem) => {
          if (isSortingActive) {
            return;
          }
          if (!draggingItem) {
            return;
          }

          const { idx, ids } = draggingItem;
          if (!ids) {
            return;
          }

          let newIndex = idx;

          if (isFilterActive) {
            if (idx === 0) {
              // handle dropping at start
              newIndex = unfilteredEntryIds.indexOf(entryIds[0]);
            } else if (idx >= entryIds.length) {
              // handle dropping at end
              newIndex = unfilteredEntryIds.indexOf(entryIds[entryIds.length - 1]) + 1;
            } else {
              // handle dropping in between
              newIndex = unfilteredEntryIds.indexOf(entryIds[idx - 1]) + 1;
            }
          }

          batch(() => {
            collabDatabaseBatch(store, contentId, () => {
              void dispatch(
                addChangeToDraft(contentId, 'main', {
                  type: DatabaseChangeType.REORDER_ENTRIES,
                  entryIds: ids,
                  newIndex,
                })
              );
            });
          });
          void client.sendRowReorderedEvent({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        },
        [
          client,
          contentId,
          dispatch,
          isSortingActive,
          isFilterActive,
          entryIds,
          unfilteredEntryIds,
          store,
        ]
      );

      const topPadding = useMemo(
        () =>
          (document.getElementById('layout-toolbar-wrapper')?.offsetHeight ??
            TOOLBAR_PADDING_FALLBACK) +
          (document.getElementById('table-layout-header-row')?.offsetHeight ??
            HEADER_ROW_PADDING_FALLBACK) +
          10 - // additional padding to ensure correct drag position
          (containerTop > 0 ? containerTop + 60 : 0), // remove some padding if the scrollable container is not at the top to be able to insert rows at the last position
        [containerTop]
      );

      const { handleDragEnd, handleDrag } = useDragItems({
        dragDirection: 'vertical',
        ids: entryIds,
        rowOrColIds: rowIds,
        contentId,
        positions: rowRefMap.positions,
        dragType: DragType.ENTRY_ROWS,
        onDrop,
        onDragOver: setDragOver,
        onSetDraggingItem: setDraggingItem,
        topOrLeftPadding: topPadding,
      });

      const prevIsDragOver = usePrevious(!!dragOver);
      const animatedItemIds = useAnimateItems({
        itemIds: rowIds,
        itemRefMap: rowRefMap,
        renderSection,
        transitionTime: dragOver || prevIsDragOver ? '0s' : undefined,
      });
      const renderRowIds = useMemo<Set<string>>(
        () =>
          new Set([
            ...animatedItemIds,
            ...(entryIdForFocusedComment ? [entryIdForFocusedComment] : []),
          ]),
        [animatedItemIds, entryIdForFocusedComment]
      );

      /**
       * The rows that should actually be rendered. These include visible rows, rows that need to be animated, a row
       * that is currently being dragged, and gap rows.
       */
      const rowsToRender = useMemo(() => {
        let gapHeight = 0;
        const rows: Array<
          { type: 'gap'; height: number } | { type: 'row'; rowId: string }
        > = [];
        for (const rowId of rowIds) {
          if (renderRowIds.has(rowId)) {
            if (gapHeight > 0) {
              rows.push({ type: 'gap', height: gapHeight });
              gapHeight = 0;
            }

            rows.push({ type: 'row', rowId });
          } else {
            gapHeight += rowRefMap.positions[rowId].height;
          }
        }
        if (gapHeight > 0) {
          rows.push({ type: 'gap', height: gapHeight });
        }
        return rows;
      }, [rowIds, renderRowIds, rowRefMap.positions]);

      const columnWidths = useMemo(() => {
        const totalColumnWidths = sum(currentWidths.filter((width) => width != null));
        const numberOfNullColumns = currentWidths.filter((width) => width == null).length;
        const flexWidth = Math.max(
          TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
          (containerWidth - totalColumnWidths) / numberOfNullColumns
        );
        return currentWidths.map((width) => (width != null ? width : flexWidth));
      }, [containerWidth, currentWidths]);

      /**
       * Fill dragged values from previously selected cells.
       **/
      useEffect(
        () =>
          monitorForElements({
            canMonitor: ({ source }) => source.data.type === DragType.FILL_VALUES,
            onDrop: ({ source }: { source: ElementDragPayload }) => {
              const selectedFieldIds = selectSelectedFieldIds(store.getState(), {
                contentId,
              });
              const selectedEntryIds = selectSelectedEntryIds(store.getState(), {
                contentId,
              });
              const sourceFieldIds = source.data.selectedFieldIds as string[];
              const sourceEntryIds = source.data.selectedEntryIds as string[];

              dispatch(
                doFillValues(
                  contentId,
                  'main',
                  selectedFieldIds,
                  selectedEntryIds,
                  sourceEntryIds,
                  sourceFieldIds
                )
              );
            },
          }),
        [contentId, dispatch, store]
      );

      useEffect(() => {
        return combine(
          monitorForElements({
            canMonitor({ source }) {
              return source.data.type === DragType.ENTRY_ROWS;
            },
            onDrop({ location, source }) {
              const dropTargets = location.current.dropTargets;
              // always want last element in drop targets array
              const destination = dropTargets[dropTargets.length - 1];
              if (!destination) {
                return;
              }

              const startIndex = extractIndex(source.data);
              const indexOfTarget = extractIndex(destination.data);

              if (startIndex === null || indexOfTarget === null) {
                return;
              }

              const closestEdgeOfTarget = extractClosestEdge(destination.data);

              onDrop({
                idx: indexOfTarget + (closestEdgeOfTarget === 'bottom' ? 1 : 0),
                ids: selectedEntries,
              });
            },
          })
        );
      }, [onDrop, selectedEntries]);

      const showEntryPrefix = shouldShowEntryPrefix(view);
      const hasNumberedEntries = useHasNumberedEntries({ contentId }) && showEntryPrefix;

      return (
        <>
          <tbody ref={tableBodyRef}>
            {allFieldsHidden ? (
              <TableLayoutEmptyStateRow
                contentId={contentId}
                allFieldsHidden={allFieldsHidden}
                canEditContent={canEditContent}
              />
            ) : (
              rowsToRender.map((row, i) =>
                row.type === GAP_ITEM ? (
                  <EntrySkeleton
                    key={`${GAP_ITEM}-${i}`}
                    hasNumberedEntries={hasNumberedEntries}
                    index={i}
                    fieldCount={fieldCount}
                    row={row}
                    columnWidths={columnWidths}
                  />
                ) : row.rowId === ADD_ITEM ? null : row.rowId === EMPTY_ITEM ? (
                  <TableLayoutEmptyStateRow
                    key={row.rowId}
                    contentId={contentId}
                    ref={rowRefMap.refs[EMPTY_ITEM]}
                    canEditContent={canEditContent}
                  />
                ) : row.rowId === DRAG_ITEM &&
                  dragOver &&
                  draggingItem &&
                  row.rowId === DRAG_ITEM ? null : (
                  <TableLayoutRow
                    canEditContent={canEditContent}
                    canEditFields={canEditFields}
                    containerWidth={containerWidth}
                    contentId={contentId}
                    currentViewId={view.id}
                    editingIds={editingIds}
                    entryId={row.rowId}
                    entryIsSelectedMap={entryIsSelectedMap}
                    index={entryIds.indexOf(row.rowId)}
                    isContentEditable={isContentEditable}
                    isDraggingActive={!!draggingItem}
                    isLastInColumn={entryIds[entryIds.length - 1] === row.rowId}
                    key={row.rowId}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    ref={rowRefMap.refs[row.rowId]}
                    setEditingIds={setEditingIds}
                  />
                )
              )
            )}
          </tbody>
        </>
      );
    }
  )
);

export default TableLayoutRows;
